import { FunctionComponent, ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { DraftConfiguration, DraftConfigurationColor } from '@/types';
import { trackFeature } from '@/utils/adobeLaunch';
import { isDraftDesignConfiguration } from '@/model/configuration/isDraftConfigurationType';
import { isDigital } from '@/model/printType/isDigital';
import { getDigitalColors } from '@/redux/core/getters/getDigitalColors';
import { useReduxDraftDispatch } from '@/redux/draft/draftDispatch';
import { ColorPicker } from '@/components/common/ColorPicker';
import { PrintColor, PrintColorButton } from '@/components/common/PrintColor';
import { setPrintColorAction } from '@/components/draft/draftState/actions/setPrintColorAction';
import './ColorSelector.scss';

interface TabButtonProps {
  children: ReactNode;
  selected: boolean;
  onClick: () => void;
}

const TabButton: FunctionComponent<TabButtonProps> = ({ selected, onClick, children }) => (
  <button
    type="button"
    className={cn('configuration-layers__tab', { 'configuration-layers__tab--selected': selected })}
    onClick={onClick}
    role="option"
    aria-selected={selected}
  >
    {children}
  </button>
);

interface ColorSelectorProps {
  configuration: DraftConfiguration;
}

export const ColorSelector: FunctionComponent<ColorSelectorProps> = ({ configuration }) => {
  const { printColors, printType: currentPrintType } = configuration;
  const digitalColors = useSelector(getDigitalColors);
  const dispatch = useReduxDraftDispatch();

  const [currentLayer, setCurrentLayer] = useState(0);

  if (
    currentPrintType === null ||
    printColors.length === 0 ||
    (isDraftDesignConfiguration(configuration) && configuration.design.restrictions.fixedColors)
  ) {
    return null;
  }

  const getColorName = (color: DraftConfigurationColor) => {
    if (color.name) {
      return color.name;
    }
    const foundColor = digitalColors.find((c) => c.fill === color.fill);
    return foundColor ? foundColor.name : color.fill;
  };

  const handleLayerSelect = (index: number) => {
    setCurrentLayer(index);
  };

  const handleColorSelect = (color: DraftConfigurationColor) => {
    trackFeature('print-colour-select');
    dispatch(setPrintColorAction(configuration, color, currentLayer));
  };

  const colors = isDigital(currentPrintType) ? digitalColors : currentPrintType.colors;
  const currentFill = printColors[currentLayer].fill;

  return (
    <div className="color-selector">
      <div className="configuration-layers" role="listbox">
        {printColors.map((color, index) => (
          <TabButton
            selected={currentLayer === index}
            onClick={() => handleLayerSelect(index)}
            key={index}
          >
            <PrintColor printType={currentPrintType} color={color} />
            <div className="color-selector__name ellipsis">{getColorName(color)}</div>
          </TabButton>
        ))}
      </div>

      <div className="color-selector__colors" role="listbox">
        <ColorPicker
          fill={currentFill}
          colors={colors}
          printType={currentPrintType}
          onChange={handleColorSelect}
        />
        {colors.map((color) => (
          <PrintColorButton
            key={color.id || color.fill}
            printType={currentPrintType}
            color={color}
            selected={color.fill === currentFill}
            onClick={() => handleColorSelect(color)}
          />
        ))}
      </div>
    </div>
  );
};
